import type {BaseFrameLayout,BaseCustomFrameSettings} from '../baseCustomFrame';
import {FrameBlurCache} from './smoothFrameBlur';
import {FRAME_REFRACTION_DISTANCE,FRAME_REFRACTION_GLSL} from './frameRefraction';

export interface GlassBackgroundRect { x:number;y:number;width:number;height:number }
let renderer: GlassRenderer | null = null;
let unavailable = false;
const frostCache=new FrameBlurCache();
export function disposeBaseFrameGlass():void { renderer?.dispose();renderer=null;unavailable=false;frostCache.clear(); }
export function getBaseFrameGlassStatus():string { return unavailable?'canvas-fallback':renderer?'webgl':'not-initialized'; }

/** GPU pixels are copied into the same Canvas used by PNG/MP4, never a CSS-only backdrop. */
export function drawBaseFrameGlass(ctx:CanvasRenderingContext2D,background:CanvasImageSource,layout:BaseFrameLayout,
  settings:BaseCustomFrameSettings,colors:[number[],number[]],blurScale:number,bgRect?:GlassBackgroundRect,shine=1,revision=''):boolean {
  if(unavailable)return false;
  try {
    renderer ||= new GlassRenderer();
    renderer.draw(ctx,background,layout,settings,colors,blurScale,bgRect,shine,revision);
    return true;
  } catch {
    renderer?.dispose();renderer=null;unavailable=true;return false;
  }
}

class GlassRenderer {
  private canvas=document.createElement('canvas');
  private gl:WebGLRenderingContext;
  private program:WebGLProgram;
  private texture:WebGLTexture;
  private buffer:WebGLBuffer;
  private maxSize:number;
  private textureWidth=0;
  private textureHeight=0;
  private uniforms=new Map<string,WebGLUniformLocation|null>();
  constructor(){
    const gl=this.canvas.getContext('webgl',{alpha:true,premultipliedAlpha:true,preserveDrawingBuffer:false,antialias:false,depth:false,stencil:false});
    if(!gl)throw Error('WebGL is unavailable');this.gl=gl;
    const viewport=gl.getParameter(gl.MAX_VIEWPORT_DIMS) as Int32Array;
    this.maxSize=Math.min(gl.getParameter(gl.MAX_TEXTURE_SIZE),gl.getParameter(gl.MAX_RENDERBUFFER_SIZE),viewport[0],viewport[1]);
    const shader=(type:number,source:string)=>{const s=gl.createShader(type)!;gl.shaderSource(s,source);gl.compileShader(s);return s;};
    const vertex=shader(gl.VERTEX_SHADER,'attribute vec2 position;void main(){gl_Position=vec4(position,0.,1.);}');
    const fragment=shader(gl.FRAGMENT_SHADER,FRAGMENT);
    const program=gl.createProgram()!;gl.attachShader(program,vertex);gl.attachShader(program,fragment);gl.linkProgram(program);
    gl.deleteShader(vertex);gl.deleteShader(fragment);
    if(!gl.getProgramParameter(program,gl.LINK_STATUS)){gl.deleteProgram(program);gl.getExtension('WEBGL_lose_context')?.loseContext();throw Error('Glass shader could not link');}
    this.program=program;this.texture=gl.createTexture()!;this.buffer=gl.createBuffer()!;
    gl.bindBuffer(gl.ARRAY_BUFFER,this.buffer);gl.bufferData(gl.ARRAY_BUFFER,new Float32Array([-1,-1,1,-1,-1,1,1,1]),gl.STATIC_DRAW);
    gl.useProgram(program);const location=gl.getAttribLocation(program,'position');gl.enableVertexAttribArray(location);gl.vertexAttribPointer(location,2,gl.FLOAT,false,0,0);
    gl.bindTexture(gl.TEXTURE_2D,this.texture);
    gl.texParameteri(gl.TEXTURE_2D,gl.TEXTURE_WRAP_S,gl.CLAMP_TO_EDGE);gl.texParameteri(gl.TEXTURE_2D,gl.TEXTURE_WRAP_T,gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D,gl.TEXTURE_MIN_FILTER,gl.LINEAR);gl.texParameteri(gl.TEXTURE_2D,gl.TEXTURE_MAG_FILTER,gl.LINEAR);
  }
  private uniform(name:string){if(!this.uniforms.has(name))this.uniforms.set(name,this.gl.getUniformLocation(this.program,name));return this.uniforms.get(name)!;}
  draw(ctx:CanvasRenderingContext2D,background:CanvasImageSource,layout:BaseFrameLayout,settings:BaseCustomFrameSettings,colors:[number[],number[]],scale:number,bgRect?:GlassBackgroundRect,shine=1,revision=''){
    const gl=this.gl,w=ctx.canvas.width,h=ctx.canvas.height;
    if(gl.isContextLost())throw Error('Glass context lost');
    const sw=Number((background as any).width)||w,sh=Number((background as any).height)||h;
    if(Math.max(w,h,sw,sh)>this.maxSize)throw Error('Glass exceeds this device texture limit');
    if(this.canvas.width!==w)this.canvas.width=w;if(this.canvas.height!==h)this.canvas.height=h;
    const crop=bgRect||{x:0,y:0,width:sw,height:sh};
    // Blur in source coordinates, then refract the continuous image. Camera
    // scaling is included so Static / Parallax have the same material softness.
    const frosted=frostCache.get(background,settings.blur*scale*crop.width/w,settings.blur*scale*crop.height/h,revision);
    const fw=Number((frosted as any).width),fh=Number((frosted as any).height);
    gl.viewport(0,0,w,h);gl.disable(gl.BLEND);gl.disable(gl.SCISSOR_TEST);gl.clearColor(0,0,0,0);gl.clear(gl.COLOR_BUFFER_BIT);
    gl.useProgram(this.program);gl.activeTexture(gl.TEXTURE0);gl.bindTexture(gl.TEXTURE_2D,this.texture);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL,1);gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL,1);
    if(this.textureWidth===fw&&this.textureHeight===fh)gl.texSubImage2D(gl.TEXTURE_2D,0,0,0,gl.RGBA,gl.UNSIGNED_BYTE,frosted as TexImageSource);
    else {gl.texImage2D(gl.TEXTURE_2D,0,gl.RGBA,gl.RGBA,gl.UNSIGNED_BYTE,frosted as TexImageSource);this.textureWidth=fw;this.textureHeight=fh;}
    gl.uniform1i(this.uniform('background'),0);gl.uniform2f(this.uniform('resolution'),w,h);
    gl.uniform4f(this.uniform('outerRect'),layout.outer.x,layout.outer.y,layout.outer.width,layout.outer.height);
    gl.uniform2f(this.uniform('radii'),layout.outer.radius,layout.inner.radius);
    gl.uniform1f(this.uniform('border'),layout.border);
    gl.uniform3fv(this.uniform('color1'),new Float32Array(colors[0]));gl.uniform3fv(this.uniform('color2'),new Float32Array(colors[1]));
    gl.uniform1f(this.uniform('angle'),settings.angle*Math.PI/180);gl.uniform1f(this.uniform('tint'),settings.opacity/100);
    gl.uniform1f(this.uniform('shine'),shine);
    gl.uniform1f(this.uniform('rimWidth'),Math.max(.65,Math.min(layout.border*.2,1.25*scale)));
    gl.uniform1f(this.uniform('bend'),settings.refraction/100*FRAME_REFRACTION_DISTANCE*scale);
    gl.uniform4f(this.uniform('backgroundRect'),crop.x/sw,crop.y/sh,crop.width/sw,crop.height/sh);
    const left=Math.max(0,Math.floor(layout.outer.x-2)),top=Math.max(0,Math.floor(layout.outer.y-2));
    const right=Math.min(w,Math.ceil(layout.outer.x+layout.outer.width+2)),bottom=Math.min(h,Math.ceil(layout.outer.y+layout.outer.height+2));
    if(right>left&&bottom>top){gl.enable(gl.SCISSOR_TEST);gl.scissor(left,h-bottom,right-left,bottom-top);gl.drawArrays(gl.TRIANGLE_STRIP,0,4);gl.disable(gl.SCISSOR_TEST);}
    ctx.drawImage(this.canvas,0,0);
  }
  dispose(){this.gl.deleteTexture(this.texture);this.gl.deleteBuffer(this.buffer);this.gl.deleteProgram(this.program);this.gl.getExtension('WEBGL_lose_context')?.loseContext();this.canvas.width=this.canvas.height=1;}
}

const FRAGMENT=`precision highp float;
uniform sampler2D background;
uniform vec2 resolution, radii;
uniform vec4 outerRect, backgroundRect;
uniform float border, angle, tint, bend, shine, rimWidth;
uniform vec3 color1,color2;
float roundedSdf(vec2 p,vec2 halfSize,float radius){vec2 q=abs(p)-halfSize+radius;return length(max(q,0.))+min(max(q.x,q.y),0.)-radius;}
${FRAME_REFRACTION_GLSL}
vec4 sampleBackground(vec2 p){vec2 uv=backgroundRect.xy+(p/resolution)*backgroundRect.zw;return texture2D(background,vec2(clamp(uv.x,0.,1.),1.-clamp(uv.y,0.,1.)));}
void main(){
 vec2 pixel=vec2(gl_FragCoord.x,resolution.y-gl_FragCoord.y);
 vec2 local=pixel-(outerRect.xy+outerRect.zw*.5),halfSize=outerRect.zw*.5;
 float outside=roundedSdf(local,halfSize,radii.x),inside=roundedSdf(local,max(halfSize-border,vec2(.0001)),radii.y);
 float coverage=(1.-smoothstep(-.7,.7,outside))*smoothstep(-.7,.7,inside);
 if(coverage<=.001||border<=.001)discard;
 vec2 corner=local-clamp(local,-halfSize+radii.x,halfSize-radii.x);
 vec2 normal=normalize(corner+vec2(.00001));
 float phase=clamp(-outside/max(border,.01),0.,1.);
 // One outer bevel, not a symmetric lens. Displacement always points outward
 // and fades monotonically into an exactly flat inner strip. No inverse curl
 // next to footage, no thin-border saturation of the Refraction control.
 vec2 refracted=pixel+normal*bend*outerBevel(phase);
 vec4 s=sampleBackground(refracted);
 vec2 direction=vec2(cos(angle),sin(angle));float ramp=clamp(.5+dot(local,direction)/max(dot(halfSize,abs(direction))*2.,1.),0.,1.);
 vec3 paint=mix(color1,color2,ramp);
 // A restrained coating reflection at the OUTER rim only. No broad white
 // lobe across the glass thickness and no second highlight beside the footage.
 float rimDistance=max(0.,-outside)/rimWidth;
 float facing=.2+.8*max(0.,dot(normal,normalize(vec2(-.6,-.8))));
 float highlight=clamp(exp(-2.*rimDistance*rimDistance)*.12*facing*shine,0.,.24);
 float alpha=(s.a*(1.-tint)+tint);vec3 rgb=alpha>.0001?(s.rgb*(1.-tint)+paint*tint)/alpha:vec3(0.);
 rgb=mix(rgb,vec3(1.),highlight);alpha=alpha+(1.-alpha)*highlight;
 gl_FragColor=vec4(rgb*alpha*coverage,alpha*coverage);
}`;
